import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { CircularProgress } from "@mui/material";
import { MAX_SIZE_MB } from "../../helper";

// Styles for modal
const customModalStyles = {
  content: {
    padding: "24px",
    width: "90%",
    maxWidth: "800px",
    maxHeight: "90vh",
    margin: "auto",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    overflowY: "auto",
  },
};

const Updates = () => {
  const [updates, setUpdates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUpdateId, seteditUpdateId] = useState(null);
  const [updatesFetchLoading, setUpdatesFetchLoading] = useState(false);
  const [buttonLoading, setbuttonLoading] = useState(false);
  const [sortByLatest, setSortByLatest] = useState(false);

  // Up to 3 Images State
  const [imageFiles, setImageFiles] = useState([null, null, null]);
  const [imagePreviews, setImagePreviews] = useState([null, null, null]);
  const [existingImages, setExistingImages] = useState([]);

  // Up to 3 Attachments State
  const [attachmentFiles, setAttachmentFiles] = useState([null, null, null]);
  const [attachmentTitles, setAttachmentTitles] = useState(["", "", ""]);
  const [existingAttachments, setExistingAttachments] = useState([]);

  const [formData, setformData] = useState({
    title: "",
    description: "",
    type: "",
    category: "",
    tag: "",
    date: "",
    time: "",
    venue: "",
    mapLink: "",
    registerURI: "",
    eventURI: "",
    public: false,
  });

  const [titleError, setTitleError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchUpdates();
  }, []);

  const filteredUpdates = updates.filter((update) =>
    filter === "all" ? true : update.type === filter
  );

  // Fetch all updates
  const fetchUpdates = async () => {
    setUpdatesFetchLoading(true);
    try {
      const response = await axiosInstance.get("updates/all");
      setUpdates(response.data.data);
    } catch (error) {
      console.error("Error fetching updates:", error);
    }
    setUpdatesFetchLoading(false);
  };

  const resetAllMedia = () => {
    setImageFiles([null, null, null]);
    setImagePreviews([null, null, null]);
    setExistingImages([]);
    setAttachmentFiles([null, null, null]);
    setAttachmentTitles(["", "", ""]);
    setExistingAttachments([]);
  };

  const resetFormState = () => ({
    title: "",
    description: "",
    type: "",
    category: "",
    tag: "",
    date: "",
    time: "",
    venue: "",
    mapLink: "",
    registerURI: "",
    eventURI: "",
    public: false,
  });

  // Handle image file change for specific slot (0, 1, 2)
  const handleImageSlotChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > MAX_SIZE_MB) {
      alert(`Image size exceeds ${MAX_SIZE_MB} MB. Please select a smaller image.`);
      e.target.value = "";
      return;
    }

    const newFiles = [...imageFiles];
    newFiles[index] = file;
    setImageFiles(newFiles);

    const newPreviews = [...imagePreviews];
    newPreviews[index] = URL.createObjectURL(file);
    setImagePreviews(newPreviews);
  };

  // Remove newly selected image from slot
  const handleRemoveImageSlot = (index) => {
    const newFiles = [...imageFiles];
    newFiles[index] = null;
    setImageFiles(newFiles);

    const newPreviews = [...imagePreviews];
    newPreviews[index] = null;
    setImagePreviews(newPreviews);
  };

  // Remove existing image from edit state
  const handleRemoveExistingImage = (indexToRemove) => {
    setExistingImages((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  // Handle attachment file change for specific slot (0, 1, 2)
  const handleAttachmentSlotChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > 25) {
      alert("Attachment size exceeds 25 MB. Please select a smaller document.");
      e.target.value = "";
      return;
    }

    const newFiles = [...attachmentFiles];
    newFiles[index] = file;
    setAttachmentFiles(newFiles);

    // Auto-fill title if blank
    if (!attachmentTitles[index]) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      const newTitles = [...attachmentTitles];
      newTitles[index] = nameWithoutExt;
      setAttachmentTitles(newTitles);
    }
  };

  const handleAttachmentTitleChange = (index, value) => {
    const newTitles = [...attachmentTitles];
    newTitles[index] = value;
    setAttachmentTitles(newTitles);
  };

  const handleRemoveAttachmentSlot = (index) => {
    const newFiles = [...attachmentFiles];
    newFiles[index] = null;
    setAttachmentFiles(newFiles);

    const newTitles = [...attachmentTitles];
    newTitles[index] = "";
    setAttachmentTitles(newTitles);
  };

  const handleRemoveExistingAttachment = (indexToRemove) => {
    setExistingAttachments((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  // Build FormData for Create / Edit
  const buildFormData = (isEdit = false) => {
    const fd = new FormData();

    Object.keys(formData).forEach((key) => {
      fd.append(key, formData[key] !== null && formData[key] !== undefined ? formData[key] : "");
    });

    // Images
    imageFiles.forEach((file, idx) => {
      if (file) {
        fd.append(`img_${idx + 1}`, file);
        if (idx === 0) {
          fd.append("img", file); // backward compatibility
        }
      }
    });

    if (isEdit) {
      fd.append("existing_images", JSON.stringify(existingImages));
      fd.append("existing_attachments", JSON.stringify(existingAttachments));
    }

    // Attachments
    attachmentFiles.forEach((file, idx) => {
      if (file) {
        fd.append(`attachment_${idx + 1}`, file);
        fd.append(`attachment_title_${idx + 1}`, attachmentTitles[idx] || file.name);
      }
    });

    return fd;
  };

  const handleCreateUpdate = async (e) => {
    e.preventDefault();
    if (titleError) {
      alert("Please fix the errors before submitting the form.");
      return;
    }
    setbuttonLoading(true);

    try {
      const formDataToSend = buildFormData(false);
      await axiosInstance.post("updates/create", formDataToSend);
      fetchUpdates();
      setShowCreateModal(false);
      setformData(resetFormState());
      resetAllMedia();
      Swal.fire("Success!", "Update created successfully with images & attachments!", "success");
    } catch (error) {
      console.error("Error creating update:", error);
      const message = error.response?.data?.message || "Failed to create update";
      Swal.fire("Error", message, "error");
    }
    setbuttonLoading(false);
  };

  // Handle edit update
  const handleEditUpdate = async (e) => {
    e.preventDefault();
    if (titleError) {
      alert("Please fix the errors before submitting the form.");
      return;
    }
    setbuttonLoading(true);

    try {
      const formDataToSend = buildFormData(true);
      formDataToSend.append("_method", "PUT");
      await axiosInstance.post(`updates/change/${editUpdateId}`, formDataToSend);
      fetchUpdates();
      setShowEditModal(false);
      resetAllMedia();
      Swal.fire("Success!", "Update updated successfully!", "success");
    } catch (error) {
      console.error("Error updating update:", error);
      const message = error.response?.data?.message || "Failed to update update";
      Swal.fire("Error", message, "error");
    }
    setbuttonLoading(false);
  };

  // Handle delete update
  const handleDeleteUpdate = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (result.isConfirmed) {
        await axiosInstance.delete(`updates/delete/${id}`);
        fetchUpdates();
        Swal.fire("Deleted!", "Update deleted successfully", "success");
      }
    } catch (error) {
      console.error("Error deleting update:", error);
      Swal.fire("Error", "Failed to delete update", "error");
    }
  };

  async function handleViewUpdate(id) {
    try {
      resetAllMedia();
      seteditUpdateId(id);
      const response = await axiosInstance.get(`updates/single/${id}`);
      const data = response?.data?.data;

      setformData({
        title: data.title || "",
        description: data.description || "",
        type: data.type || "",
        category: data.category || "",
        tag: data.tag || "",
        date: data.date || "",
        time: data.time || "",
        venue: data.venue || "",
        mapLink: data.mapLink || "",
        registerURI: data.registerURI || "",
        eventURI: data.eventURI || "",
        public: data.public || false,
      });

      // Populate existing images
      if (data.images && Array.isArray(data.images) && data.images.length > 0) {
        setExistingImages(data.images);
      } else if (data.img) {
        setExistingImages([{ url: data.img, public_id: data.cloudinaryImgId }]);
      }

      // Populate existing attachments
      if (data.attachments && Array.isArray(data.attachments) && data.attachments.length > 0) {
        setExistingAttachments(data.attachments);
      }

      setShowEditModal(true);
    } catch (error) {
      console.error("Error fetching update:", error);
      Swal.fire({
        icon: "error",
        title: "Error fetching update details!",
        text: "Please try again.",
        timer: 1500,
      });
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "title") {
      const hasSpecialCharacters = /[^a-zA-Z0-9 &,'\-.:()]/.test(value);
      if (hasSpecialCharacters) {
        setTitleError("Some special characters are not allowed in the title.");
      } else {
        setTitleError("");
      }
    }
    setformData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const sortedUpdates = sortByLatest
    ? [...filteredUpdates].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    : filteredUpdates || [];

  // Helper UI component for Image Upload Section (Up to 3 images)
  const renderImageUploadSection = (isEdit = false) => {
    return (
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: "#f8fafc",
          marginBottom: "20px",
        }}
      >
        <div style={{ marginBottom: "12px" }}>
          <h4 style={{ margin: "0 0 4px 0", fontSize: "15px", color: "#1e293b", fontWeight: "bold" }}>
            📸 Event / News Images (Up to 3 Images)
          </h4>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
            Upload up to 3 high-resolution images. Image 1 serves as the primary cover image, while Images 2 &amp; 3 form the gallery.
          </p>
        </div>

        {/* Existing Images (Edit Mode) */}
        {isEdit && existingImages.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", color: "#475569", marginBottom: "8px" }}>
              Current Uploaded Images ({existingImages.length}/3):
            </label>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {existingImages.map((imgItem, idx) => (
                <div
                  key={idx}
                  style={{
                    position: "relative",
                    width: "120px",
                    height: "90px",
                    borderRadius: "6px",
                    overflow: "hidden",
                    border: "2px solid #cbd5e1",
                    backgroundColor: "#000",
                  }}
                >
                  <img
                    src={imgItem.url || imgItem}
                    alt={`Existing ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      bottom: "2px",
                      left: "4px",
                      backgroundColor: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      fontSize: "10px",
                      padding: "1px 4px",
                      borderRadius: "3px",
                    }}
                  >
                    Slot {idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingImage(idx)}
                    style={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      background: "#ef4444",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      fontSize: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Remove Image"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3 Upload Slots */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
          {[0, 1, 2].map((slotIdx) => {
            const previewUrl = imagePreviews[slotIdx];

            return (
              <div
                key={slotIdx}
                style={{
                  border: "1px dashed #cbd5e1",
                  borderRadius: "8px",
                  padding: "10px",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <span style={{ fontSize: "12px", fontWeight: "bold", color: slotIdx === 0 ? "#0284c7" : "#475569", display: "block", marginBottom: "6px" }}>
                    {slotIdx === 0 ? "Image 1 (Main Cover)" : `Image ${slotIdx + 1} (Gallery)`}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSlotChange(slotIdx, e)}
                    style={{ fontSize: "12px", width: "100%", marginBottom: "6px" }}
                  />
                </div>

                {previewUrl && (
                  <div style={{ position: "relative", marginTop: "6px", width: "100%", height: "80px", borderRadius: "4px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <img
                      src={previewUrl}
                      alt={`Preview ${slotIdx + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImageSlot(slotIdx)}
                      style={{
                        position: "absolute",
                        top: "2px",
                        right: "2px",
                        background: "#ef4444",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        fontSize: "11px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      title="Clear slot"
                    >
                      &times;
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper UI component for Attachments Upload Section (Up to 3 attachments)
  const renderAttachmentUploadSection = (isEdit = false) => {
    return (
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: "#f8fafc",
          marginBottom: "20px",
        }}
      >
        <div style={{ marginBottom: "12px" }}>
          <h4 style={{ margin: "0 0 4px 0", fontSize: "15px", color: "#1e293b", fontWeight: "bold" }}>
            📎 Attachments &amp; Documents (Up to 3 Files)
          </h4>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
            Attach up to 3 downloadable documents (PDF, Word, Excel, Notifications, Registration Forms, Circulars).
          </p>
        </div>

        {/* Existing Attachments in Edit mode */}
        {isEdit && existingAttachments.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", color: "#475569", marginBottom: "8px" }}>
              Current Attachments ({existingAttachments.length}/3):
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {existingAttachments.map((attItem, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    padding: "8px 12px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                    <span style={{ fontSize: "18px" }}>📄</span>
                    <div>
                      <a
                        href={attItem.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: "13px", fontWeight: "bold", color: "#2563eb", textDecoration: "none" }}
                      >
                        {attItem.title || attItem.file_name || `Attachment ${idx + 1}`}
                      </a>
                      <span style={{ fontSize: "11px", color: "#64748b", marginLeft: "8px" }}>
                        ({attItem.file_size || "Document"})
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingAttachment(idx)}
                    style={{
                      backgroundColor: "#fee2e2",
                      color: "#dc2626",
                      border: "1px solid #fca5a5",
                      borderRadius: "4px",
                      padding: "3px 8px",
                      fontSize: "11px",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3 Attachment Slots */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {[0, 1, 2].map((attIdx) => {
            const hasNewFile = !!attachmentFiles[attIdx];

            return (
              <div
                key={attIdx}
                style={{
                  border: "1px dashed #cbd5e1",
                  borderRadius: "8px",
                  padding: "12px",
                  backgroundColor: "#ffffff",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <span style={{ fontSize: "12px", fontWeight: "bold", color: "#334155" }}>
                    Attachment Slot {attIdx + 1}
                  </span>
                  {hasNewFile && (
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachmentSlot(attIdx)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#ef4444",
                        fontSize: "11px",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                    >
                      Clear File
                    </button>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "11px", color: "#64748b", marginBottom: "3px" }}>
                      Document Title / Label
                    </label>
                    <input
                      type="text"
                      placeholder={`e.g. Schedule / Circular / Notice ${attIdx + 1}`}
                      value={attachmentTitles[attIdx] || ""}
                      onChange={(e) => handleAttachmentTitleChange(attIdx, e.target.value)}
                      style={{
                        width: "100%",
                        padding: "6px 8px",
                        fontSize: "12px",
                        borderRadius: "4px",
                        border: "1px solid #cbd5e1",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "11px", color: "#64748b", marginBottom: "3px" }}>
                      Select File (PDF, Word, Excel, etc.)
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,image/*"
                      onChange={(e) => handleAttachmentSlotChange(attIdx, e)}
                      style={{ fontSize: "12px", width: "100%" }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "10px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <button
          onClick={() => {
            resetAllMedia();
            setformData(resetFormState());
            setShowCreateModal(true);
          }}
          style={{
            padding: "10px 20px",
            backgroundColor: "#16a34a",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontWeight: "bold",
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          + Create New Update
        </button>

        <button
          onClick={() => setSortByLatest((prev) => !prev)}
          style={{
            padding: "8px 16px",
            backgroundColor: sortByLatest ? "#2563eb" : "#f1f5f9",
            color: sortByLatest ? "#fff" : "#334155",
            border: "1px solid #cbd5e1",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          {sortByLatest ? "Showing Latest" : "Sort by Latest"}
        </button>
      </div>

      {/* Filter Tabs */}
      <div style={{ marginBottom: "20px", display: "flex", gap: "8px" }}>
        {[
          { key: "all", label: "All Updates" },
          { key: "news", label: "News" },
          { key: "events", label: "Events" },
          { key: "announcement", label: "Announcements" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              padding: "8px 16px",
              backgroundColor: filter === key ? "#0f172a" : "#f8fafc",
              color: filter === key ? "#fff" : "#475569",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "13px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Updates Cards Grid */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        {updatesFetchLoading ? (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 9999,
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          sortedUpdates.map((update) => {
            const imagesCount = (update.images && update.images.length) || (update.img ? 1 : 0);
            const attachmentsCount = (update.attachments && update.attachments.length) || 0;

            return (
              <div
                key={update._id}
                style={{
                  width: "270px",
                  padding: "14px",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.06)",
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", marginBottom: "10px", borderRadius: "6px", overflow: "hidden", backgroundColor: "#f1f5f9" }}>
                    <img
                      src={update.img || (update.images && update.images[0]?.url) || "/uo-16-9.webp"}
                      alt={update.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <div style={{ position: "absolute", top: "6px", left: "6px", display: "flex", gap: "4px" }}>
                      <span
                        style={{
                          backgroundColor: "rgba(15, 23, 42, 0.8)",
                          color: "#fff",
                          fontSize: "10px",
                          fontWeight: "bold",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          textTransform: "uppercase",
                        }}
                      >
                        {update.type}
                      </span>
                    </div>
                  </div>

                  <h3 style={{ fontSize: "14px", fontWeight: "bold", color: "#1e293b", margin: "0 0 6px 0", lineHeight: "1.4" }}>
                    {update.title}
                  </h3>

                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
                    <span
                      style={{
                        backgroundColor: "#e0f2fe",
                        color: "#0369a1",
                        fontSize: "11px",
                        fontWeight: "600",
                        padding: "2px 6px",
                        borderRadius: "4px",
                      }}
                    >
                      🖼️ {imagesCount} {imagesCount === 1 ? "Image" : "Images"}
                    </span>
                    {attachmentsCount > 0 && (
                      <span
                        style={{
                          backgroundColor: "#fef3c7",
                          color: "#92400e",
                          fontSize: "11px",
                          fontWeight: "600",
                          padding: "2px 6px",
                          borderRadius: "4px",
                        }}
                      >
                        📎 {attachmentsCount} {attachmentsCount === 1 ? "Doc" : "Docs"}
                      </span>
                    )}
                  </div>

                  <p style={{ fontSize: "12px", color: "#64748b", margin: "0 0 10px 0" }}>
                    📅 {update.date} {update.time ? `• ${update.time}` : ""}
                  </p>
                </div>

                <Actions
                  courseId={update._id}
                  onEdit={handleViewUpdate}
                  onDelete={handleDeleteUpdate}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Create Update Modal */}
      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => setShowCreateModal(false)}
        style={customModalStyles}
        contentLabel="Create New Update"
      >
        <h2 style={{ marginTop: 0, color: "#0f172a", borderBottom: "1px solid #e2e8f0", paddingBottom: "10px" }}>
          Create New Update
        </h2>
        <form onSubmit={handleCreateUpdate}>
          {[
            { label: "Title", name: "title", type: "text", required: true },
            { label: "Description", name: "description", type: "textarea" },
            { label: "Type", name: "type", type: "dropdown", required: true, options: ["news", "events", "announcement"] },
            { label: "Category", name: "category", type: "text" },
            { label: "Tag", name: "tag", type: "text" },
            { label: "Date", name: "date", type: "date", required: true },
            { label: "Time", name: "time", type: "text", condition: (fd) => fd.type === "events" },
            { label: "Venue", name: "venue", type: "text", condition: (fd) => fd.type === "events" },
            { label: "Map Link", name: "mapLink", type: "text", condition: (fd) => fd.type === "events" },
            { label: "Register URI", name: "registerURI", type: "text", condition: (fd) => fd.type === "events" },
            { label: "Event URI", name: "eventURI", type: "text", condition: (fd) => fd.type === "events" },
          ].map(({ label, name, type, required, options, condition }) => {
            if (condition && !condition(formData)) {
              return null;
            }
            return (
              <div key={name} style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontWeight: "bold", fontSize: "13px", marginBottom: "4px", color: "#334155" }}>
                  {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
                </label>
                {type === "dropdown" ? (
                  <select
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    required={required}
                    style={{
                      width: "100%",
                      padding: "8px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                    }}
                  >
                    <option value="">Select an option</option>
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : type === "textarea" ? (
                  <textarea
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    required={required}
                    rows={4}
                    style={{
                      width: "100%",
                      padding: "8px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                    }}
                  />
                ) : (
                  <input
                    type={type}
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    required={required}
                    style={{
                      width: "100%",
                      padding: "8px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                    }}
                  />
                )}
                {name === "title" && titleError && (
                  <span style={{ color: "red", fontSize: "12px" }}>{titleError}</span>
                )}
              </div>
            );
          })}

          {/* Up to 3 Images Upload */}
          {renderImageUploadSection(false)}

          {/* Up to 3 Attachments Upload */}
          {renderAttachmentUploadSection(false)}

          <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              id="create_public"
              name="public"
              checked={formData.public}
              style={{ transform: "scale(1.2)" }}
              onChange={handleChange}
            />
            <label htmlFor="create_public" style={{ fontWeight: "bold", fontSize: "13px", color: "#334155", cursor: "pointer" }}>
              Public (Visible to everyone)
            </label>
          </div>

          <button
            type="submit"
            disabled={buttonLoading}
            style={{
              padding: "10px 24px",
              backgroundColor: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {buttonLoading ? <CircularProgress size={20} color="inherit" /> : "Create Update"}
          </button>
        </form>
      </Modal>

      {/* Edit Update Modal */}
      <Modal
        isOpen={showEditModal}
        onRequestClose={() => {
          resetAllMedia();
          setShowEditModal(false);
        }}
        style={customModalStyles}
        contentLabel="Edit Update"
      >
        <h2 style={{ marginTop: 0, color: "#0f172a", borderBottom: "1px solid #e2e8f0", paddingBottom: "10px" }}>
          Edit Update
        </h2>
        <form onSubmit={handleEditUpdate}>
          {[
            { label: "Title", name: "title", type: "text", required: true },
            { label: "Description", name: "description", type: "textarea" },
            { label: "Type", name: "type", type: "dropdown", required: true, options: ["news", "events", "announcement"] },
            { label: "Category", name: "category", type: "text" },
            { label: "Tag", name: "tag", type: "text" },
            { label: "Date", name: "date", type: "date", required: true },
            { label: "Time", name: "time", type: "text", condition: (fd) => fd.type === "events" },
            { label: "Venue", name: "venue", type: "text", condition: (fd) => fd.type === "events" },
            { label: "Map Link", name: "mapLink", type: "text", condition: (fd) => fd.type === "events" },
            { label: "Register URI", name: "registerURI", type: "text", condition: (fd) => fd.type === "events" },
            { label: "Event URI", name: "eventURI", type: "text", condition: (fd) => fd.type === "events" },
          ].map(({ label, name, type, required, options, condition }) => {
            if (condition && !condition(formData)) {
              return null;
            }
            return (
              <div key={name} style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontWeight: "bold", fontSize: "13px", marginBottom: "4px", color: "#334155" }}>
                  {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
                </label>
                {type === "dropdown" ? (
                  <select
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    required={required}
                    style={{
                      width: "100%",
                      padding: "8px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                    }}
                  >
                    <option value="">Select an option</option>
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : type === "textarea" ? (
                  <textarea
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    required={required}
                    rows={4}
                    style={{
                      width: "100%",
                      padding: "8px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                    }}
                  />
                ) : (
                  <input
                    type={type}
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    required={required}
                    style={{
                      width: "100%",
                      padding: "8px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                    }}
                  />
                )}
                {name === "title" && titleError && (
                  <span style={{ color: "red", fontSize: "12px" }}>{titleError}</span>
                )}
              </div>
            );
          })}

          {/* Up to 3 Images Upload */}
          {renderImageUploadSection(true)}

          {/* Up to 3 Attachments Upload */}
          {renderAttachmentUploadSection(true)}

          <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              id="edit_public"
              name="public"
              checked={!!formData.public}
              style={{ transform: "scale(1.2)" }}
              onChange={handleChange}
            />
            <label htmlFor="edit_public" style={{ fontWeight: "bold", fontSize: "13px", color: "#334155", cursor: "pointer" }}>
              Public (Visible to everyone)
            </label>
          </div>

          <button
            type="submit"
            disabled={buttonLoading}
            style={{
              padding: "10px 24px",
              backgroundColor: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {buttonLoading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default Updates;
